import { delay } from 'base/helpers';
import { computedFn } from 'mobx-utils';
import { Assistant } from '../ai/assistant';
import { isUserWrittenName } from '../ai/page_sections';
import { Page, Project, Space } from '../model';
import { pageAndAllSubpages } from '../model_helpers';
import { ProjectSession } from '../session/session';

export async function fillInMissingComponents(
  project: Project,
  selectedSpace: Space | 'home',
  assistant: Assistant,
  session: ProjectSession,
  abort: AbortSignal,
) {
  const attemptedPages = new Set<Page>();

  while (!abort.aborted) {
    // Limit the number of concurrent tasks
    if (assistant.taskLog.pendingTasks.length > 3) {
      await delay(50);
      continue;
    }

    const pages = pageAndAllSubpages(
      selectedSpace === 'home' ? project.homePage : selectedSpace,
    );
    const page = pages.find(
      (page) =>
        isMissingComponents(page, project) &&
        !attemptedPages.has(page) &&
        !assistant.isChoosingComponents(page) &&
        session.canViewWireframe(page),
    );
    if (!page) return; // all done!
    assistant.chooseComponents(page, { generateCopy: true });
    // If the assistant fails to pick components, we don't want to retry in a loop
    attemptedPages.has(page);
    await delay(50);
  }
}

const isMissingComponents = computedFn((page: Page, project: Project) => {
  if (!isUserWrittenName(page.name)) return false;
  return page.sections.some((sref) => {
    const section =
      sref.type === 'inline' ? sref.value : project.globalSections.get(sref.id);
    return (
      section &&
      isUserWrittenName(section.name) &&
      section.element.current == null
    );
  });
});
